import {
    AddRounded,
    ExpandMoreRounded,
    RefreshRounded,
    WarningAmberRounded,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Pagination,
    Skeleton,
    styled,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { AcxTabPanel } from "components/UI/AcxTabPanel";
import { RawJsonPanel } from "./RawJsonPanel";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useDebounce from "utils/useDebounce";
import { useStore } from "utils/useStore";
import { KboFlowDebugService } from "services/KboFlowDebugService";
import { UserForSharing, UserService } from "services/UserService";
import { EmailSuggestion } from "components/UI/AcxEmailChipInput";
import { ScheduleWorkflowDialog } from "./ScheduleWorkflowDialog";
import {
    KboFlowRunDebug,
    KboFlowRunOutput,
    KboFlowRunSummary,
    getKboFlowRunStatusPresentation,
} from "./KboFlowRunDebug";
import {
    formatDate,
    formatDuration,
    formatFilenameDate,
    formatNumber,
    shortId,
} from "./KboFlowDebugUI";
import {
    ContextSection,
    FindingsSection,
    InvocationsSection,
    OverviewSection,
    QuestionsSection,
    ReportSection,
    ValidationSection,
} from "./KboFlowRunSections";
import { KboFlowAnalysisJourneySection } from "./KboFlowAnalysisJourneySection";
import { ExplorationSection } from "./KboFlowExplorationSection";

const PAGE_SIZE = 10;
const PAGE_PARAM = "kfdPage";
const RUNS_PARAM = "kfdRuns";

const PageHeader = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[700],
    fontSize: "24px",
    fontWeight: 600,
}));

const TAB_LABELS = [
    "Overview",
    "Context",
    "Questions",
    "Validation",
    "Findings",
    "Journey",
    "Report",
    "Invocations",
    "Exploration",
    "Raw JSON",
] as const;

type RerunPhase = {
    phase: string;
    label: string;
};

const RERUN_PHASES: RerunPhase[] = [
    { phase: "QuestionGeneration", label: "Rerun Questions" },
    { phase: "QuestionValidation", label: "Rerun Validation" },
    { phase: "Analysis", label: "Rerun Analysis" },
    { phase: "ReportAssembly", label: "Rerun Report" },
];

function isRunOutputObject(
    runOutput: KboFlowRunDebug["runOutput"],
): runOutput is KboFlowRunOutput {
    return !!runOutput && typeof runOutput === "object";
}

/* ----------------------------- URL param codec ---------------------------- */

/** Parse the `kfdRuns` param (`id:tab,id:tab`) into a Map of open run id → tab index. */
function decodeRuns(raw: string | null): Map<string, number> {
    const map = new Map<string, number>();
    if (!raw) return map;
    for (const token of raw.split(",")) {
        if (!token) continue;
        const [id, tab] = token.split(":");
        if (!id) continue;
        const parsed = Number.parseInt(tab ?? "0", 10);
        map.set(id, Number.isNaN(parsed) ? 0 : parsed);
    }
    return map;
}

/** Serialize an open-runs map back to the `kfdRuns` param value ("" when empty). */
function encodeRuns(map: Map<string, number>): string {
    return Array.from(map.entries())
        .map(([id, tab]) => `${id}:${tab}`)
        .join(",");
}

/* -------------------------------- run panel ------------------------------- */

function RunSummary({ summary }: { summary: KboFlowRunSummary }) {
    const cachedInputTokens = summary.cachedInputTokenCount;
    const statusPresentation = getKboFlowRunStatusPresentation(summary);
    const hasCompletedNodeFailures =
        statusPresentation.isCompleted &&
        summary.failedExplorationNodeCount > 0;
    return (
        <Box
            sx={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                pr: 2,
                width: "100%",
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 170 }}>
                {formatDate(summary.createdOn)}
            </Typography>
            <Chip
                size="small"
                color={statusPresentation.color}
                label={statusPresentation.label}
            />
            {hasCompletedNodeFailures && (
                <Tooltip title="This run completed despite node-level failures. Its reports use results from completed branches.">
                    <Chip
                        size="small"
                        color="warning"
                        icon={<WarningAmberRounded />}
                        label={`${summary.failedExplorationNodeCount} node ${
                            summary.failedExplorationNodeCount === 1
                                ? "failure"
                                : "failures"
                        }`}
                        variant="outlined"
                    />
                </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            {summary.mode && (
                <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={summary.mode}
                />
            )}
            {summary.version === 1 && (
                <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label="Legacy v1 · view only"
                />
            )}
            <Chip
                size="small"
                variant="outlined"
                label={`run ${shortId(summary.runId ?? summary.id)}`}
            />
            <Chip
                size="small"
                variant="outlined"
                label={`user ${shortId(summary.subjectUserId)}`}
            />
            <Chip
                size="small"
                variant="outlined"
                label={formatDuration(
                    summary.startedAtUtc,
                    summary.completedAtUtc,
                )}
            />
            <Chip
                size="small"
                variant="outlined"
                label={`${summary.generatedQuestionCount} Q`}
            />
            <Chip
                size="small"
                variant="outlined"
                label={`${summary.generatedReportCount} report${
                    summary.generatedReportCount === 1 ? "" : "s"
                }`}
            />
            <Chip
                size="small"
                variant="outlined"
                label={`${summary.analysisFindingCount} ${
                    summary.mode === "Cohort Refinement"
                        ? "refinements"
                        : "findings"
                }`}
            />
            {cachedInputTokens > 0 && (
                <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`${formatNumber(cachedInputTokens)} cached`}
                />
            )}
        </Box>
    );
}

type RunPanelProps = {
    summary: KboFlowRunSummary;
    full?: KboFlowRunDebug;
    fullLoading: boolean;
    fullError?: string;
    expanded: boolean;
    activeTab: number;
    onToggle: (id: string, open: boolean) => void;
    onTabChange: (id: string, tab: number) => void;
    onRerunFromPhase: (runId: string, phase: string) => void;
    rerunSubmitting: boolean;
    onSendReportEmail: (
        runId: string,
        recipients: string[],
        reportIndex: number,
    ) => Promise<boolean>;
    emailSubmitting: boolean;
    emailSuggestions: EmailSuggestion[];
    emailSuggestionsLoading: boolean;
};

const RunPanel = React.memo(function RunPanel({
    summary,
    full,
    fullLoading,
    fullError,
    expanded,
    activeTab,
    onToggle,
    onTabChange,
    onRerunFromPhase,
    rerunSubmitting,
    onSendReportEmail,
    emailSubmitting,
    emailSuggestions,
    emailSuggestionsLoading,
}: RunPanelProps) {
    const output = isRunOutputObject(full?.runOutput)
        ? full.runOutput
        : undefined;
    const isRefinement = output?.refinementResults !== undefined;
    const isMultiLevelAnalysis = output?.analysisResults !== undefined;

    // Loading state for the run detail while its full output is fetched. `alternate`
    // is required: AcxLoadingIndicator's default branch passes an invalid
    // react-spinners color and renders as blank whitespace, whereas the named
    // spinner branches use a valid themed color.
    const loadingIndicator = (
        <Box
            sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                p: 6,
            }}
        >
            <AcxLoadingIndicator
                size={32}
                color="primary"
                alternate="PuffLoader"
            />
        </Box>
    );

    return (
        <Accordion
            id={`kbo-run-${summary.id}`}
            expanded={expanded}
            onChange={(_event, isExpanded) => onToggle(summary.id, isExpanded)}
            disableGutters
            TransitionProps={{ unmountOnExit: true }}
            sx={{
                "&:before": { display: "none" },
                border: "1px solid",
                borderColor: "lightgrayBorder.main",
                borderRadius: 1,
                overflow: "hidden",
                scrollMarginTop: 16,
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                sx={{ backgroundColor: "gray.100" }}
            >
                <RunSummary summary={summary} />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                {fullLoading ? (
                    loadingIndicator
                ) : fullError ? (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="error">
                            {fullError}
                        </Typography>
                    </Box>
                ) : output ? (
                    <Box>
                        {!isRefinement && summary.version !== 1 && (
                            <Box
                                sx={{
                                    alignItems: "center",
                                    borderBottom: "1px solid",
                                    borderColor: "lightgrayBorder.main",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    justifyContent: "flex-end",
                                    px: 2,
                                    py: 1,
                                }}
                            >
                                {RERUN_PHASES.map((phase) => (
                                    <AcxButton
                                        key={phase.phase}
                                        color="white"
                                        variant="outlined"
                                        startIcon={<RefreshRounded />}
                                        loading={rerunSubmitting}
                                        onClick={() =>
                                            onRerunFromPhase(
                                                summary.id,
                                                phase.phase,
                                            )
                                        }
                                    >
                                        {phase.label}
                                    </AcxButton>
                                ))}
                            </Box>
                        )}
                        <Tabs
                            value={activeTab}
                            onChange={(_event, next) =>
                                onTabChange(summary.id, next)
                            }
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                borderBottom: "1px solid",
                                borderColor: "lightgrayBorder.main",
                                px: 1,
                            }}
                        >
                            {TAB_LABELS.map((label) => {
                                const displayLabel =
                                    isRefinement && label === "Findings"
                                        ? "Refinement"
                                        : isMultiLevelAnalysis &&
                                          label === "Findings"
                                        ? "Analysis"
                                        : label;
                                return <Tab key={label} label={displayLabel} />;
                            })}
                        </Tabs>
                        <Box sx={{ p: 2 }}>
                            {/* Render only the active tab so the heavy rich-JSON
                                view mounts on demand, not on every expand. */}
                            <AcxTabPanel value={activeTab} index={0}>
                                {activeTab === 0 && (
                                    <OverviewSection output={output} />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={1}>
                                {activeTab === 1 && (
                                    <ContextSection output={output} />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={2}>
                                {activeTab === 2 && (
                                    <QuestionsSection output={output} />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={3}>
                                {activeTab === 3 && (
                                    <ValidationSection output={output} />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={4}>
                                {activeTab === 4 && (
                                    <FindingsSection output={output} />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={5}>
                                {activeTab === 5 &&
                                    (isMultiLevelAnalysis ? (
                                        <KboFlowAnalysisJourneySection
                                            results={
                                                output.analysisResults ?? []
                                            }
                                        />
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            The simplified journey view is
                                            only available for hierarchical
                                            (multi-level) analysis runs.
                                        </Typography>
                                    ))}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={6}>
                                {activeTab === 6 && (
                                    <ReportSection
                                        output={output}
                                        onSendReportEmail={(
                                            recipients,
                                            reportIndex,
                                        ) =>
                                            onSendReportEmail(
                                                summary.id,
                                                recipients,
                                                reportIndex,
                                            )
                                        }
                                        emailSubmitting={emailSubmitting}
                                        emailSuggestions={emailSuggestions}
                                        emailSuggestionsLoading={
                                            emailSuggestionsLoading
                                        }
                                    />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={7}>
                                {activeTab === 7 && (
                                    <InvocationsSection output={output} />
                                )}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={8}>
                                {activeTab === 8 &&
                                    (output.exploration ? (
                                        <ExplorationSection
                                            exploration={output.exploration}
                                        />
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            The exploration view is only
                                            available for runs that produced an
                                            exploration tree (version 5+).
                                        </Typography>
                                    ))}
                            </AcxTabPanel>
                            <AcxTabPanel value={activeTab} index={9}>
                                {activeTab === 9 && (
                                    <RawJsonPanel
                                        value={full?.runOutput}
                                        downloadFileName={`KboFlowRun-${formatFilenameDate(
                                            summary.createdOn,
                                            output.runId ?? summary.id,
                                        )}`}
                                    />
                                )}
                            </AcxTabPanel>
                        </Box>
                    </Box>
                ) : full ? (
                    <Box sx={{ p: 2 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                        >
                            Run output could not be parsed as JSON. Showing the
                            raw value below.
                        </Typography>
                        <RawJsonPanel
                            value={full.runOutput}
                            downloadFileName={`KboFlowRun-${formatFilenameDate(
                                summary.createdOn,
                                summary.id,
                            )}`}
                        />
                    </Box>
                ) : (
                    // Expanded but the full output has not been fetched yet (the
                    // fetch effect runs a tick after expand); show the loader
                    // instead of a blank panel to avoid a whitespace flash.
                    loadingIndicator
                )}
            </AccordionDetails>
        </Accordion>
    );
});

/** Collapsed-row placeholder shown while a page of runs is loading. */
function RunRowSkeleton() {
    return (
        <Skeleton
            variant="rounded"
            height={52}
            animation="wave"
            sx={{ borderRadius: 1 }}
        />
    );
}

/* ---------------------------------- page ---------------------------------- */

export const KboFlowDebug: React.FC<{ orgId: string }> = ({ orgId }) => {
    const service = useMemo(() => new KboFlowDebugService(), []);
    const userService = useMemo(() => new UserService(), []);
    const messageStore = useStore(MessageStore);
    const [summaries, setSummaries] = useState<KboFlowRunSummary[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [fullRunsById, setFullRunsById] = useState<
        Map<string, KboFlowRunDebug>
    >(new Map());
    const [runLoadingIds, setRunLoadingIds] = useState<Set<string>>(new Set());
    const [runErrorById, setRunErrorById] = useState<Map<string, string>>(
        new Map(),
    );
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [rerunningRunId, setRerunningRunId] = useState<string | null>(null);
    const [sendingEmailRunId, setSendingEmailRunId] = useState<string | null>(
        null,
    );
    const [users, setUsers] = useState<UserForSharing[]>([]);
    const [emailSuggestions, setEmailSuggestions] = useState<EmailSuggestion[]>(
        [],
    );
    const [emailSuggestionsLoading, setEmailSuggestionsLoading] =
        useState(false);

    // Local UI state is the source of truth for instant interactions; it is
    // initialized once from the URL and synced back (debounced) below.
    const [openMap, setOpenMap] = useState<Map<string, number>>(() =>
        decodeRuns(searchParams.get(RUNS_PARAM)),
    );
    const [page, setPageState] = useState<number>(() => {
        const parsed = Number.parseInt(searchParams.get(PAGE_PARAM) ?? "1", 10);
        return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    });

    const requestedPage = Math.max(1, page);
    useEffect(() => {
        const controller = new AbortController();
        (async () => {
            try {
                setIsLoading(true);
                setError(null);
                // A fresh page load invalidates any cached full run outputs.
                setFullRunsById(new Map());
                setRunErrorById(new Map());
                setRunLoadingIds(new Set());
                const result = await service.getRuns(
                    requestedPage,
                    PAGE_SIZE,
                    controller.signal,
                );
                setSummaries(result?.runs ?? []);
                setTotalCount(result?.totalCount ?? 0);
            } catch (err) {
                if (!controller.signal.aborted) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Failed to load Kbo Flow runs.",
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        })();
        return () => controller.abort();
    }, [service, reloadToken, requestedPage]);

    // Load the org's users once, to suggest as report-email recipients. Reuses the
    // shared "users for sharing" endpoint (excludes agents/disabled users).
    useEffect(() => {
        if (!orgId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                setEmailSuggestionsLoading(true);
                const users = await userService.getUsersForSharing(orgId);
                if (cancelled) return;
                setUsers(users);
                setEmailSuggestions(
                    users
                        .filter(
                            (u) =>
                                u.email &&
                                u.email !== "acxmachine@authenticx.com",
                        )
                        .map((u) => ({
                            email: u.email,
                            name: `${u.firstName} ${u.lastName}`.trim(),
                        })),
                );
            } catch {
                // Suggestions are a convenience; typing addresses still works.
                if (!cancelled) {
                    setUsers([]);
                    setEmailSuggestions([]);
                }
            } finally {
                if (!cancelled) setEmailSuggestionsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userService, orgId]);

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const clampedPage = Math.min(Math.max(1, page), totalPages);

    // Snap the page back into range if the URL/state points past the last page.
    useEffect(() => {
        if (page > totalPages) setPageState(totalPages);
    }, [page, totalPages]);

    // Fetch the full output (once) for any run that is expanded on the current page.
    const ensureFullRun = useCallback(
        (id: string) => {
            if (fullRunsById.has(id) || runLoadingIds.has(id)) return;
            setRunLoadingIds((prev) => new Set(prev).add(id));
            service
                .getRun(id)
                .then((full) => {
                    setFullRunsById((prev) => new Map(prev).set(id, full));
                    setRunErrorById((prev) => {
                        if (!prev.has(id)) return prev;
                        const next = new Map(prev);
                        next.delete(id);
                        return next;
                    });
                })
                .catch((err) => {
                    setRunErrorById((prev) =>
                        new Map(prev).set(
                            id,
                            err instanceof Error
                                ? err.message
                                : "Failed to load run output.",
                        ),
                    );
                })
                .finally(() => {
                    setRunLoadingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    });
                });
        },
        [service, fullRunsById, runLoadingIds],
    );

    useEffect(() => {
        summaries.forEach((summary) => {
            if (openMap.has(summary.id)) ensureFullRun(summary.id);
        });
    }, [summaries, openMap, ensureFullRun]);

    // Debounced sync: local state -> URL (replace, preserving other params).
    const runsParam = useMemo(() => encodeRuns(openMap), [openMap]);
    const pageParam = clampedPage <= 1 ? "" : String(clampedPage);
    const debouncedRuns = useDebounce(runsParam, 300);
    const debouncedPage = useDebounce(pageParam, 300);
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (debouncedRuns) next.set(RUNS_PARAM, debouncedRuns);
        else next.delete(RUNS_PARAM);
        if (debouncedPage) next.set(PAGE_PARAM, debouncedPage);
        else next.delete(PAGE_PARAM);
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [debouncedRuns, debouncedPage, searchParams, setSearchParams]);

    const setPage = useCallback((value: number) => setPageState(value), []);

    const toggleRun = useCallback((id: string, open: boolean) => {
        setOpenMap((prev) => {
            const next = new Map(prev);
            if (open) {
                if (!next.has(id)) next.set(id, 0);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    const setRunTab = useCallback((id: string, tab: number) => {
        setOpenMap((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Map(prev);
            next.set(id, tab);
            return next;
        });
    }, []);

    const rerunFromPhase = useCallback(
        async (runId: string, phase: string) => {
            try {
                setRerunningRunId(runId);
                await service.rerunWorkflow(runId, {
                    rerunFromPhase: phase,
                });
                messageStore.logMessage("KboFlow rerun scheduled", "success");
                setReloadToken((token) => token + 1);
            } catch (error) {
                messageStore.logError(
                    error instanceof Error
                        ? error.message
                        : "Failed to schedule KboFlow rerun",
                );
            } finally {
                setRerunningRunId(null);
            }
        },
        [messageStore, service],
    );

    const sendReportEmail = useCallback(
        async (
            runId: string,
            recipients: string[],
            reportIndex: number,
        ): Promise<boolean> => {
            const fullRun = fullRunsById.get(runId);
            const reportId = isRunOutputObject(fullRun?.runOutput)
                ? fullRun.runOutput.reports?.[reportIndex]?.reportId
                : undefined;
            if (!reportId) {
                messageStore.logError(
                    "This legacy report has no persisted email report ID and cannot be sent.",
                );
                return false;
            }

            try {
                setSendingEmailRunId(runId);
                const { sentTo } = await service.sendReportEmail(
                    runId,
                    recipients,
                    reportId,
                );
                messageStore.logMessage(
                    `Report sent to ${sentTo.join(", ")}`,
                    "success",
                );
                return true;
            } catch (error) {
                messageStore.logError(
                    error instanceof Error
                        ? error.message
                        : "Failed to send report email",
                );
                return false;
            } finally {
                setSendingEmailRunId(null);
            }
        },
        [fullRunsById, messageStore, service],
    );

    // The server already returns just this page's rows.
    const pageRuns = summaries;
    const rangeStart = totalCount === 0 ? 0 : (clampedPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(rangeStart + pageRuns.length - 1, totalCount);

    return (
        <Box sx={{ p: 3, width: "100%" }}>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <PageHeader>Kbo Flow Debug</PageHeader>
                {!isLoading && !error && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${totalCount} run${
                            totalCount === 1 ? "" : "s"
                        }`}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <AcxButton
                    color="white"
                    variant="outlined"
                    startIcon={<RefreshRounded />}
                    loading={isLoading}
                    onClick={() => setReloadToken((token) => token + 1)}
                >
                    Refresh
                </AcxButton>
                <AcxButton
                    color="primary"
                    startIcon={<AddRounded />}
                    onClick={() => setScheduleOpen(true)}
                >
                    Schedule Workflow
                </AcxButton>
            </Box>
            <ScheduleWorkflowDialog
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                service={service}
                users={users}
                onScheduled={() => setReloadToken((token) => token + 1)}
            />
            {error ? (
                <Typography color="error">{error}</Typography>
            ) : !isLoading && totalCount === 0 ? (
                <Typography color="text.secondary">
                    No Kbo Flow runs found for this organization.
                </Typography>
            ) : (
                <>
                    {!isLoading && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                        >
                            Showing {rangeStart}–{rangeEnd} of {totalCount}
                        </Typography>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        {isLoading
                            ? Array.from({
                                  length: pageRuns.length || PAGE_SIZE,
                              }).map((_, index) => (
                                  <RunRowSkeleton key={index} />
                              ))
                            : pageRuns.map((run) => (
                                  <RunPanel
                                      key={run.id}
                                      summary={run}
                                      full={fullRunsById.get(run.id)}
                                      fullLoading={runLoadingIds.has(run.id)}
                                      fullError={runErrorById.get(run.id)}
                                      expanded={openMap.has(run.id)}
                                      activeTab={openMap.get(run.id) ?? 0}
                                      onToggle={toggleRun}
                                      onTabChange={setRunTab}
                                      onRerunFromPhase={rerunFromPhase}
                                      rerunSubmitting={
                                          rerunningRunId === run.id
                                      }
                                      onSendReportEmail={sendReportEmail}
                                      emailSubmitting={
                                          sendingEmailRunId === run.id
                                      }
                                      emailSuggestions={emailSuggestions}
                                      emailSuggestionsLoading={
                                          emailSuggestionsLoading
                                      }
                                  />
                              ))}
                    </Box>
                    {totalPages > 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 2,
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={clampedPage}
                                onChange={(_event, value) => setPage(value)}
                                color="primary"
                                disabled={isLoading}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default KboFlowDebug;
